import { useEffect, useEffectEvent } from "react";
import type { WizardAPI } from "./types";

export type UseControlledWizardStateProps = {
  currentStepIndex: number;
  setCurrentStepIndex: (updater: (previousIndex: number) => number) => void;
};

type OnWizardStepChange<TStep extends string | number | symbol> = (options: {
  step: TStep | undefined;
  wizardAPI: WizardAPI<TStep>;
}) => void | Promise<void>;

type UseControlledWizardStateEventProps<
  TStep extends string | number | symbol,
> = {
  onStepChange?: OnWizardStepChange<TStep>;
  onComplete?: () => void | Promise<void>;
};

export type UseControlledWizardProps<TStep extends string | number | symbol> =
  UseControlledWizardStateProps &
    UseControlledWizardStateEventProps<TStep> & {
      steps: readonly TStep[];
    };

export const useControlledWizard = <TStep extends string | number | symbol>({
  steps,
  currentStepIndex,
  setCurrentStepIndex,
  onStepChange,
  onComplete,
}: UseControlledWizardProps<TStep>): WizardAPI<TStep> => {
  const currentStep = steps[currentStepIndex];

  const goToNextStep = () => {
    if (currentStep !== undefined && currentStepIndex === steps.length - 1) {
      void onComplete?.();
      return;
    }

    setCurrentStepIndex((prevCurrentStepIndex) =>
      Math.min(prevCurrentStepIndex + 1, Math.max(steps.length - 1, 0))
    );
  };

  const goToPreviousStep = () => {
    setCurrentStepIndex((prevCurrentStepIndex) =>
      Math.max(prevCurrentStepIndex - 1, 0)
    );
  };

  const wizardAPI: WizardAPI<TStep> = {
    currentStep: steps[currentStepIndex],
    currentStepIndex,
    goToNextStep,
    goToPreviousStep,
  };

  const onWizardStepListener = useEffectEvent((step: TStep | undefined) =>
    onStepChange?.({ step, wizardAPI })
  );
  useEffect(() => {
    void onWizardStepListener(currentStep);
  }, [currentStep]);

  return wizardAPI;
};
